import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Minus, Trash2 } from 'lucide-react';
import { DropdownItemOption } from './ItemDropdown';
import { ItemDropdown } from './ItemDropdown';
import { UnitDropdown } from './UnitDropdown';
import { tg } from '../../telegram';

interface AddWasteItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  ingredients: DropdownItemOption[];
  units: string[];
  onAdd: (item: {
    item_name: string;
    quantity: number;
    unit: string;
    reason: string;
  }) => void;
}

const COMMON_REASONS = [
  'Hỏng / Hết hạn',
  'Đổ vỡ / Rơi rớt',
  'Pha lỗi / Hỏng mẻ',
  'Mốc / Chảy nước',
];

export const AddWasteItemModal: React.FC<AddWasteItemModalProps> = ({
  isOpen,
  onClose,
  ingredients,
  units,
  onAdd,
}) => {
  const [selectedName, setSelectedName] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [unit, setUnit] = useState<string>('g');
  const [reason, setReason] = useState<string>('Hỏng / Hết hạn');

  useEffect(() => {
    if (isOpen) {
      setSelectedName('');
      setQuantity(1);
      setUnit('g');
      setReason('Hỏng / Hết hạn');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleSelectIngredient = (name: string, defaultUnit?: string) => {
    setSelectedName(name);
    const meta = ingredients.find(
      (i) => i.name.toLowerCase() === name.trim().toLowerCase()
    );
    const resolvedUnit = meta?.unit || defaultUnit || 'g';
    setUnit(resolvedUnit);
  };

  const currentQty = Math.max(0, quantity);
  const isValid = Boolean(selectedName.trim() && currentQty > 0);

  const handleConfirmAdd = () => {
    if (!isValid) return;
    tg.haptic.success();
    onAdd({
      item_name: selectedName.trim(),
      quantity: currentQty,
      unit: unit.trim() || 'g',
      reason: reason.trim() || 'Hỏng / Hết hạn',
    });
    onClose();
  };

  const handleQuickAddQty = (delta: number) => {
    tg.haptic.impact('light');
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  return createPortal(
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9990,
        backgroundColor: 'rgba(21, 17, 14, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        className="modal-sheet"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          backgroundColor: 'var(--bg-surface)',
          borderRadius: '20px 20px 0 0',
          padding: '16px 20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: 'var(--shadow-lg)',
          overflowY: 'auto',
          animation: 'slideUp 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Handle */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'var(--border-color)',
            }}
          />
        </div>

        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-danger)',
              }}
            >
              <Trash2 size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>
                Thêm Món Hủy / Hao Hụt
              </h3>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Ghi nhận hao hụt nguyên liệu thô hoặc bán thành phẩm
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="stepper-btn"
            style={{ width: 32, height: 32, padding: 0, color: 'var(--text-secondary)' }}
            title="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        {/* 1. Chọn nguyên liệu */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            1. Chọn NL hoặc BTP cần hủy (*)
          </label>
          <ItemDropdown
            value={selectedName}
            items={ingredients}
            placeholder="🔍 Bấm để tìm & chọn NL hoặc BTP..."
            onChange={handleSelectIngredient}
            style={{ minHeight: 44, height: 44, fontSize: 14 }}
          />
        </div>

        {/* 2. Số lượng hủy & ĐVT */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            2. Số lượng hủy (*)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                border: '1.5px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-surface)',
                overflow: 'hidden',
                flex: 1,
                minHeight: 46,
              }}
            >
              <button
                type="button"
                onClick={() => handleQuickAddQty(-1)}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 20 }}
                title="Giảm 1"
              >
                <Minus size={18} />
              </button>

              <input
                type="number"
                min="0.1"
                step="any"
                className="num"
                value={quantity === 0 ? '' : quantity}
                onChange={(e) => setQuantity(Math.max(0, Number(e.target.value)))}
                onBlur={(e) => {
                  if (!e.target.value) setQuantity(1);
                }}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: 18,
                  color: 'var(--text-primary)',
                  backgroundColor: 'transparent',
                  padding: '4px',
                }}
              />

              <button
                type="button"
                onClick={() => handleQuickAddQty(1)}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 20 }}
                title="Tăng 1"
              >
                <Plus size={18} />
              </button>
            </div>

            <div style={{ width: 85 }}>
              <UnitDropdown
                value={unit}
                units={units}
                onChange={(newUnit) => setUnit(newUnit)}
                style={{ minHeight: 46, height: 46 }}
              />
            </div>
          </div>

          {/* Quick presets */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {[1, 5, 10, 50, 100].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setQuantity(preset);
                }}
                style={{
                  flex: 1,
                  padding: '5px 0',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-color)',
                  backgroundColor: quantity === preset ? 'var(--primary-accent)' : 'var(--bg-surface-subtle)',
                  color: quantity === preset ? '#FFFFFF' : 'var(--text-secondary)',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                {preset}
              </button>
            ))}
          </div>
        </div>

        {/* 3. Lý do hủy */}
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            3. Lý do hủy / hao hụt
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {COMMON_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setReason(r);
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-color)',
                  backgroundColor: reason === r ? 'var(--bg-active)' : 'var(--bg-surface-subtle)',
                  color: reason === r ? 'var(--primary-accent)' : 'var(--text-secondary)',
                  fontWeight: reason === r ? 800 : 600,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {r}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Hoặc gõ lý do cụ thể khác..."
            style={{ minHeight: 40, height: 40, fontSize: 13 }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            disabled={!isValid}
            onClick={handleConfirmAdd}
            className="btn btn-primary btn-full"
            style={{
              minHeight: 48,
              fontSize: 15,
              fontWeight: 700,
              gap: 8,
              opacity: isValid ? 1 : 0.5,
              cursor: isValid ? 'pointer' : 'not-allowed',
            }}
          >
            <Plus size={18} /> Thêm Vào Cuối Danh Sách
          </button>

          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary btn-full"
            style={{ minHeight: 42, fontSize: 13, fontWeight: 600 }}
          >
            Hủy Bỏ
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
